//
// puller.ts
//
// Copyright 2024-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

// github.com/couchbase/couchbase-lite-core/blob/master/modules/docs/pages/replication-protocol.adoc

import {Endpoint, type EndpointConfig, type EndpointParams} from "./endpoint";
import type { RemoteRevisionInfo, RemoteSequence, RemoteRevision, LostAccess, ReplicationFilter } from "./types";
import {RemoteSeqTracker} from "./remoteSeqTracker";
import { isJSONObject, type JSONObject } from "@/util/json_types";
import * as blip from "@/blip/blip";
import type { CBLDictionary, DocID, RevID} from "@/database/types";
import { isValidDocID, isValidRevID } from "@/database/types";
import type { BlobLoader } from "@/blob/blob";
import { AttachmentDigests, ImportAttachments } from "@/blob/sync_blobs";
import { assert } from "@/util/assert";
import type { CBLDictLike, CBLDocument } from "@/database/document";


const kDefaultSaveBatchSize = 200;


/** Callback that resolves a conflict between a local and remote (server) document revision,
 *  after a replicator pulls a conflicting revision from the server.
 *  @param local  The current local document, or `null` if it's deleted.
 *  @param remote  The conflicting remote document, or `null` if it's deleted.
 *  @returns  The resolved document, or `null` if it should be deleted.
 *            You may return either `local` or `remote`.
 *            You may modify the properties to merge the two revisions. */
export type PullConflictResolver<D extends CBLDictLike<D> = CBLDictionary> = (
    local: CBLDocument<D> | null,
    remote: CBLDocument<D> | null) => Promise<CBLDocument<D> | null>;


export interface PullerConfig extends EndpointConfig {
    channels?       : readonly string[],
    activeOnly?     : boolean,  // If true, ignore tombstones
    enableAutoPurge?: boolean,
    filter?         : ReplicationFilter,
    conflictResolver?: PullConflictResolver,
    wantBatchSize?  : number,   // Number of revisions to pass to `wantRevs` [default up to server]
    saveBatchSize?  : number,   // Number of revisions to pass to `saveRevs` [default 200]
}

export interface PullerDelegate {
    /** Rejects existing revisions by setting their `skip` property to `true`.
     *  For other revisions, it should set `knownRevs` to the current revID(s) it has. */
    wantRevs?(rev: RemoteRevisionInfo[], caughtUp: boolean): Promise<void>

    /** Saves downloaded revisions to the database. */
    saveRevs(revs: RemoteRevision[]) : Promise<boolean>

    /** Checks whether each of the digests matches a blob in the local database.
     *  If so, returns `undefined`. Otherwise returns an array of the unknown digests. */
    missingBlobs(digests: Set<string>): Promise<string[] | undefined>;

    /** Adds a blob. Implementor must verify that the contents match the digest. */
    addBlob(digest: string, contents: Uint8Array): Promise<void>;

    /** Returns true if the onDocuments callback is set.
     * When true, the pull replicator includes the revocations flag in subChanges
     * requests to Sync Gateway, allowing it to receive notifications when access
     * to a document is revoked even when auto-purge is disabled. */
    hasOnDocumentsCallback(): boolean;

    blobLoader: BlobLoader;
}


interface RemoteRevisionWithMsg extends RemoteRevision {
    readonly remoteSequence: RemoteSequence;
    readonly msg: blip.Message;
}

type MessageOrRev = blip.Message | RemoteRevisionWithMsg;


/** Pulls documents from a remote collection using the Couchbase Mobile replication protocol.
    This class has no knowledge of a local database. */
export class Puller extends Endpoint {

    constructor(params: EndpointParams,
                private readonly config: PullerConfig,
                private readonly delegate: PullerDelegate)
    {
        super(params, config, "pull");

        this.#seqTracker = new RemoteSeqTracker(this.checkpointer.remoteSequence);

        this.#pendingRevMessages = 0;
        this.#pendingInserts = 0;

        // Now get things started by sending the `subChanges` request:
        const subRequest: blip.Properties = {
            Profile:    "subChanges",
            collection: this.collectionIndex
        };
        if (this.checkpointer.remoteSequence !== undefined)
            subRequest.since = JSON.stringify(this.checkpointer.remoteSequence);
        if (config.continuous)
            subRequest.continuous = "true";
        if (this.config.channels) {
            subRequest.filter = "sync_gateway/bychannel";
            subRequest.channels = this.config.channels.join(",");
        }
        if (this.config.activeOnly)
            subRequest.activeOnly = "true";
        if (this.config.enableAutoPurge !== false || this.delegate.hasOnDocumentsCallback())
            subRequest.revocations = "true";
        if (this.config.wantBatchSize)
            subRequest.batch = this.config.wantBatchSize;

        let body;
        if (config.documentIDs)
            body = {docIDs: config.documentIDs as DocID[]};

        this.logger.debug `Sending ${JSON.stringify(subRequest)}`;

        void this.socket.send(subRequest, body);
    }


    override checkIdle() : boolean {
        assert(this.#pendingRevMessages >= 0 && this.#pendingInserts >= 0);
        return this.caughtUp
            && this.#changesQueue.length === 0
            && !this.#processingChanges
            && this.#pendingRevMessages === 0 && this.#pendingInserts === 0
            && !this.#inserting;
    }


    //-------- HANDLING CHANGES MESSAGES:

    // Number of revs to insert into db at once
    get batchSize(): number {return this.config.saveBatchSize ?? kDefaultSaveBatchSize; }


    // Handler for incoming `changes` messages:
    onChanges(msg: blip.Message) {
        if (this.canProcessChangesMessage()) {
            void this.processChangesMessage(msg);
        } else {
            this.logger.debug `Queuing changes message #${msg.number}`;
            this.#changesQueue.push(msg);
            this.statusChanged();
        }
    }

    private canProcessChangesMessage(): boolean {
        return this.socketOpen && !this.#processingChanges &&
               (this.#insertQueue?.length ?? 0) + this.#pendingRevMessages < this.batchSize + 100;
    }

    private maybeProcessChangesMessage() {
        if (this.#changesQueue.length > 0 && this.canProcessChangesMessage()) {
            const [msg] = this.#changesQueue.splice(0, 1);
            void this.processChangesMessage(msg);
        }
    }

    private async processChangesMessage(msg: blip.Message) {
        const changes = msg.bodyJSON as Array<Array<unknown>>;
        if (changes === null || changes.length === 0) {
            // Empty list of changes means we've got all the changes:
            this.logger.debug `Got 'changes'#${msg.number}: Puller has caught up`;
            this.caughtUp = true;
            void this.maybeInsertRevs();
            if (msg.wantsReply)
                this.socket.sendReplyTo(msg, {});

        } else {
            this.logger.debug `Got 'changes'#${msg.number}: ${changes.length} revs from seq ${changes[0][0]}`;

            this.#processingChanges = true;
            this.statusChanged();
            try {
                const revs = changes.map( (change) : RemoteRevisionInfo => {
                    let removal = Number(change[3] ?? 0);
                    let lostAccess: LostAccess | undefined;
                    if (removal & 2)
                        lostAccess = 'revoked';
                    else if (removal & 4)
                        lostAccess = 'removed';

                    return {
                        id:             change[1] as DocID,
                        rev:            change[2] as RevID,
                        deleted:        (removal & 1) !== 0,
                        lostAccess:     lostAccess,
                        bodySize:       change[4] ? Number(change[4]) : null,
                        remoteSequence: change[0] as RemoteSequence,
                    };
                });

                if (this.delegate.wantRevs) {
                    try {
                        await this.delegate.wantRevs(revs, this.caughtUp);
                    } catch (x) {
                        this.logger.error `wantRevs threw ${x}`;
                        this.replicator.fatalError(x as Error);
                    }
                }

                let wanted = 0;
                const noKnownRevs = Array<RevID>();
                const answers = revs.map( rev => {
                    if (!rev.skip) {
                        ++wanted;
                        ++this.#pendingRevMessages;     // Decremented by onRev()
                        this.#seqTracker.addSequence(rev.remoteSequence);
                        return rev.knownRevs || noKnownRevs;
                    } else {
                        this.#seqTracker.skipSequence(rev.remoteSequence);
                        return 0;
                    }
                });

                if (this.socketOpen) {
                    this.logger.debug `Replying to 'changes'#${msg.number} (seq ${changes[0][0]}+) asking for ${wanted}/${answers.length} revs`;
                    this.socket.sendReplyTo(msg, {maxHistory: 1}, answers);
                }
                this.checkpointer.remoteSequence = this.#seqTracker.getCheckpoint();
            } finally {
                this.#processingChanges = false;
            }
        }
        this.statusChanged();
        this.maybeProcessChangesMessage();
    }


    //-------- HANDLING REVISIONS:


    // Handler for incoming `rev` messages.
    onRev(msg: blip.Message) {
        --this.#pendingRevMessages;
        ++this.#pendingInserts;             // Decremented by finishedRev()
        this.#insertQueue.push(msg);
        void this.maybeInsertRevs();
    }


    // Inserts all the `rev` messages in `insertable` by passing them to the `saveRevs` callback.
    private async maybeInsertRevs() {
        if (this.#inserting) return;

        const batchSize = this.batchSize;

        while (this.#insertQueue.length > 0 &&
                (this.#insertQueue.length >= batchSize
                 || (this.caughtUp && this.#pendingRevMessages === 0))) {
            this.#inserting = true;
            try {
                let messages: MessageOrRev[];
                if (this.#insertQueue.length <= batchSize) {
                    messages = this.#insertQueue;
                    this.#insertQueue = [];
                } else {
                    messages = this.#insertQueue.splice(0, batchSize);
                }
                this.logger.debug `Inserting ${messages.length} of ${messages.length + this.#insertQueue.length} revs`;

                this.maybeProcessChangesMessage();  // Start downloading more

                // Decode each BLIP message to a RemoteRevisionWithMsg:
                const revs: RemoteRevisionWithMsg[] = [];
                for (const msg of messages) {
                    if (msg instanceof blip.Message) {
                        const rev = this.decodeRevMsg(msg);
                        let blobDigests = AttachmentDigests(rev.body);
                        if (blobDigests) {
                            // This rev has blobs we need to download first; don't add it yet
                            rev.hasBlobs = true;
                            void this.processRevWithBlobs(rev, blobDigests);
                        } else {
                            revs.push(rev);
                        }
                    } else {
                        // 'msg' is actually a RemoteRevisionWithMsg we got the blobs for:
                        revs.push(msg);
                    }
                }

                if (revs.length > 0) {
                    // Call the caller's `saveRevs` handler:
                    let ok = false;
                    try {
                        ok = await this.delegate.saveRevs(revs);
                        if (!ok)
                            this.logger.error `Failed to save revs`;
                    } catch (x) {
                        this.logger.error `Failed to save revs: caught ${x}`;
                        this.replicator.fatalError(x as Error);
                        return;
                    }

                    // Done! Update the sequence tracker, and send responses to `rev` messages:
                    revs.forEach( rev => this.finishedRev(rev, ok) );
                    this.checkpointer.remoteSequence = this.#seqTracker.getCheckpoint();
                    if (ok) {
                        this._progress += revs.length;
                    }
                }
            } finally {
                this.#inserting = false;
                this.logger.debug `End insertRevs`;
                this.statusChanged();
            }
        }
        this.maybeProcessChangesMessage();
    }


    // Returns a RemoteRevision object created from a `rev` message.
    private decodeRevMsg(msg: blip.Message) : RemoteRevisionWithMsg {
        const body = msg.bodyJSON as JSONObject;
        if (!isJSONObject(body))
            throw new blip.BLIPError("invalid revision body", 400);

        const id = (body._id || msg.property("id"));
        const rev = body._rev || msg.property("rev");
        if (!isValidDocID(id) || !isValidRevID(rev))
            throw new blip.BLIPError("invalid id or rev property", 400);

        const deleted = (!!body._deleted || (msg.property("deleted") !== "")) ? 1 : undefined;
        const history = msg.property("history").split(/\s*,\s*/) as RevID[];
        const remoteSequence = JSON.parse(msg.property("sequence")) as RemoteSequence;

        delete body._id;
        delete body._rev;
        delete body._deleted;

        //TODO: Set the lostAccess flag (how?)

        return {id, rev, deleted, body, history, remoteSequence, msg};
    }


    // Given a rev that contains blobs, first check with the delegate whether the blob digests
    // are known. If not, send a 'getAttachment' request to the server to download the blob.
    // Finally push the rev back into #insertable so it'll get inserted.
    private async processRevWithBlobs(rev: RemoteRevisionWithMsg, digests: Set<string>) {
        let missingBlobs = await this.delegate.missingBlobs(digests);
        if (missingBlobs && missingBlobs.length > 0) {
            this.logger.info `Downloading ${missingBlobs.length} blob(s) of doc ${rev.id}`;
            const promises = missingBlobs.map( async digest => {
                return this.socket.send({
                    Profile:    "getAttachment",
                    collection: this.collectionIndex,
                    docID:      rev.id,
                    digest:     digest,
                }).then( async reply => {
                    this.logger.info `Saving ${reply.bodyData.length}-byte blob of doc "${rev.id}"`;
                    return this.delegate.addBlob(digest, reply.bodyData);
                }, error => {
                    this.logger.error `Unable to download blob ${digest} of doc ${rev.id}: ${(error as Error).message}`;
                });
            });
            await Promise.all(promises);
        }

        // Convert SG-style _attachments to CBL-style blobs:
        ImportAttachments(rev.body, this.delegate.blobLoader);

        // It's now OK to insert this revision, so put it back in the queue:
        this.#insertQueue.push(rev);
        void this.maybeInsertRevs();
    }


    // Called when a revision has been saved or rejected.
    finishedRev(rev: RemoteRevisionWithMsg, ok: boolean) {
        const msg = rev.msg;
        if (ok) {
            this.#seqTracker.finishedSequence(rev.remoteSequence);
            if (msg.wantsReply)
                this.socket.sendReplyTo(msg, {});
        } else {
            if (msg.wantsReply)
                this.socket.sendErrorReplyTo(msg, "Failed to insert revision", 502);
        }
        if (--this.#pendingInserts === 0)
            this.statusChanged();
    }


    toString(): string { return `Puller[${this.collectionID}]`; }

    #seqTracker!        : RemoteSeqTracker;         // Manages the checkpoint
    #changesQueue       : blip.Message[] = [];      // Unhandled 'changes' messages
    #processingChanges  = false;                    // True while in processChangesMessage()
    #pendingRevMessages = 0;                        // Number of `rev` msgs I'm waiting for
    #pendingInserts     = 0;                        // Number of revs I'm waiting to insert
    #insertQueue        = new Array<MessageOrRev>;  // Revs waiting to be added to db
    #inserting          = false;                    // True while revs are being added to db
}
